<template>
    <div class="pdf-loader-viewer">
        <div v-if="loading" class="loader-container">
            <h1>{{ trans('loading_text') }}</h1>
            <p class="my-4">{{ trans('loading_subtext') }}</p>
            <div class="loader"></div>
        </div>
        <div v-else-if="pdfUrl" class="loader-container">
            <p><i class="fad fa-fw fa-file-pdf fa-4x text-success mr-2"></i></p>
            <h1 class="my-5">{{ trans('file_ready_title') }}</h1>
            <div>
                <a class="btn btn-primary" target="_blank" :href="pdfUrl" @click="closeWindow()">
                    {{ trans('download_file') }}
                </a>
            </div>
        </div>
        <div v-else-if="error" class="error-container">
            <div class="alert alert-danger" role="alert">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                {{ errorMessage }}
            </div>
        </div>
    </div>
</template>

<script>
import TranslationsUtils from "../../app-components/TranslationsUtils";
export default {
    mixins: [TranslationsUtils],
    props: {
        loaderUrl: {
            type: String,
            required: true
        },
        downloadUrl: {
            type: String,
            required: true
        },
        pollingInterval: {
            type: Number,
            default: 2000
        },
        maxAttempts: {
            type: Number,
            default: 2
        }
    },
    data() {
        return {
            loading: true,
            error: false,
            errorMessage: this.trans('error'),
            pdfUrl: null,
            pollingCount: 0,
            pollingTimer: null
        };
    },
    mounted() {
        this.startPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        startPolling() {
            this.loading = true;
            this.error = false;
            this.pdfUrl = null;
            this.pollingCount = 0;

            this.pollForPdf();
        },

        stopPolling() {
            if (this.pollingTimer) {
                clearTimeout(this.pollingTimer);
                this.pollingTimer = null;
            }
        },

        pollForPdf() {
            if (this.pollingCount >= this.maxAttempts) {
                this.handleError(this.trans('maximum_poll'));
                return;
            }

            this.pollingCount++;

            axios.get(this.loaderUrl)
            .then(response => {
                if (response.data && response.data.status === 'ok') {
                    if(!response.data.fileHash) {
                        this.handleError(this.trans('ajax_error_title') + ' ' + this.trans('file_not_generated_properly'));
                        return;
                    }

                    this.handlePdfSuccess(response.data.fileHash);
                    this.changeWindowTitle();
                } else {
                    this.scheduleNextPoll();
                }
            })
            .catch(error => {
                // If we get a 404 or other error that indicates the PDF is not ready yet,
                // continue polling. Otherwise, show an error.
                if (error.response && error.response.status === 404) {
                    this.scheduleNextPoll();
                } else {
                    this.handleError(this.trans('ajax_error_title') + ' ' + error.message);
                }
            });
        },

        closeWindow() {
            setTimeout(() => window.close(), 100); //wait 500ms
        },

        changeWindowTitle() {
            document.title = '✅ ' + this.trans('file_ready_title');
        },

        scheduleNextPoll() {
            this.pollingTimer = setTimeout(() => {
                this.pollForPdf();
            }, this.pollingInterval);
        },

        handlePdfSuccess(fileHash) {
            this.stopPolling();

            this.loading = false;
            this.pdfUrl = this.downloadUrl.replace('%fileHash%', fileHash);
        },

        handleError(message) {
            this.stopPolling();
            this.loading = false;
            this.error = true;
            this.errorMessage = message;
            this.$emit('pdf-error', message);
        },

        retry() {
            this.startPolling();
        }
    }
};
</script>

<style lang="scss" scoped>
.pdf-loader-viewer {
    width: 100%;
    height: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

    h1 {
        font-size: 1.5rem;
    }

    .loader-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 2rem;

        .loader {
            display: inline-block;
            width: 48px;
            height: 48px;
            border: 6px solid rgba(0, 0, 0, 0.1);
            border-top-color: #4273fa;
            border-radius: 50%;
            animation: spin 1.3s linear infinite;
            box-sizing: border-box;
        }

        @keyframes spin {
            to {
                transform: rotate(360deg);
            }
        }
    }

    .error-container {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 2rem;
    }

    .pdf-container {
        flex: 1;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;

        .pdf-viewer {
            width: 100%;
            height: 100%;
            min-height: 100vh;
            border: 1px solid #dee2e6;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/basic/PdfLoader.vue